import { useState, useCallback } from 'react';

/**
 * Custom hook for Plan Mode state management
 * Provides centralized state and actions for the Plan Mode workflow
 */

export type PlanModeStep = 'requirements' | 'design-system' | 'blueprint' | 'wireframes-low' | 'wireframes-high' | 'manifest' | 'jira' | 'scaffold' | 'complete';

interface PlanModeState {
  currentStep: PlanModeStep;
  blueprintId: string | null;
  manifestId: string | null;
  isGenerating: boolean;
  error: string | null;
}

export function usePlanMode() {
  const [state, setState] = useState<PlanModeState>({
    currentStep: 'requirements',
    blueprintId: null,
    manifestId: null,
    isGenerating: false,
    error: null,
  });

  const setCurrentStep = useCallback((step: PlanModeStep) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const setBlueprintId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, blueprintId: id }));
  }, []);

  const setManifestId = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, manifestId: id }));
  }, []);

  const setIsGenerating = useCallback((isGenerating: boolean) => {
    setState(prev => ({ ...prev, isGenerating }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const reset = useCallback(() => {
    setState({
      currentStep: 'requirements',
      blueprintId: null,
      manifestId: null,
      isGenerating: false,
      error: null,
    });
  }, []);

  // Auto-save to localStorage
  const saveToLocalStorage = useCallback(() => {
    if (state.blueprintId || state.manifestId) {
      localStorage.setItem('planMode_state', JSON.stringify({
        blueprintId: state.blueprintId,
        manifestId: state.manifestId,
        currentStep: state.currentStep,
      }));
    }
  }, [state.blueprintId, state.manifestId, state.currentStep]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    const saved = localStorage.getItem('planMode_state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setState(prev => ({
          ...prev,
          blueprintId: parsed.blueprintId,
          manifestId: parsed.manifestId,
          currentStep: parsed.currentStep,
        }));
        return true;
      } catch (error) {
        console.error('Failed to load saved state:', error);
        return false;
      }
    }
    return false;
  }, []);

  const clearLocalStorage = useCallback(() => {
    localStorage.removeItem('planMode_state');
  }, []);

  return {
    ...state,
    setCurrentStep,
    setBlueprintId,
    setManifestId,
    setIsGenerating,
    setError,
    reset,
    saveToLocalStorage,
    loadFromLocalStorage,
    clearLocalStorage,
  };
}
